// React
import { useCallback, useEffect } from "react";

// Third party imports
import { useDispatch, useSelector } from "react-redux";

// Redux
import {
  removeBoard,
  removeColumn,
  setBoards,
  upsertBoard,
  upsertColumn,
} from "redux/slices/boardsSlice.jsx";
import { setError } from "redux/slices/snackbarSlice.jsx";

// APIs
import {
  createBoard as createBoardApi,
  createBoardColumn,
  deleteBoard as deleteBoardApi,
  deleteBoardColumn,
  listBoards,
  reorderBoardColumns,
  updateBoard as updateBoardApi,
  updateBoardColumn,
} from "api/boards.jsx";

/**
 * Boards list (sidebar "Bacheche team") and the board/column mutations.
 * The list is loaded once and shared through redux.
 * Mutations resolve with the saved object and dispatch errors to the
 * snackbar; they re-throw so callers can keep a dialog open on failure.
 */
// Request shared by every useBoards instance while it is pending.
let inFlight = null;

const useBoards = ({ autoLoad = true } = {}) => {
  const dispatch = useDispatch();
  const { boards, loaded } = useSelector((state) => state.boards);

  const refreshBoards = useCallback(() => {
    // Several components mount together (sidebar, page, client sidebar):
    // they share one in-flight request instead of firing one each.
    if (!inFlight) {
      inFlight = listBoards()
        .then((res) => {
          dispatch(setBoards(res.data || []));
        })
        .catch((err) => {
          if (err?.response?.status === 403) {
            // No access (e.g. client users): no boards, nothing to report.
            dispatch(setBoards([]));
          } else {
            // Keep `loaded` false so the next mount retries.
            dispatch(setError(err));
          }
        })
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  }, [dispatch]);

  useEffect(() => {
    if (autoLoad && !loaded) refreshBoards();
  }, [autoLoad, loaded, refreshBoards]);

  const run = useCallback(
    async (fn) => {
      try {
        return await fn();
      } catch (err) {
        dispatch(setError(err));
        throw err;
      }
    },
    [dispatch]
  );

  const createBoard = (data) =>
    run(async () => {
      const res = await createBoardApi(data);
      dispatch(upsertBoard(res.data));
      return res.data;
    });

  const updateBoard = (boardId, data) =>
    run(async () => {
      const res = await updateBoardApi(boardId, data);
      dispatch(upsertBoard(res.data));
      return res.data;
    });

  const deleteBoard = (boardId) =>
    run(async () => {
      await deleteBoardApi(boardId);
      dispatch(removeBoard(boardId));
    });

  const createColumn = (boardId, data) =>
    run(async () => {
      const res = await createBoardColumn(boardId, data);
      dispatch(upsertColumn(res.data));
      return res.data;
    });

  const updateColumn = (boardId, columnId, data) =>
    run(async () => {
      const res = await updateBoardColumn(boardId, columnId, data);
      dispatch(upsertColumn(res.data));
      return res.data;
    });

  const deleteColumn = (boardId, columnId, moveTo) =>
    run(async () => {
      await deleteBoardColumn(boardId, columnId, moveTo);
      dispatch(removeColumn({ boardId, columnId }));
      // Cards moved server side and flags may have moved: reload the board.
      await refreshBoards();
    });

  const reorderColumns = (boardId, ids) =>
    run(async () => {
      const res = await reorderBoardColumns(boardId, ids);
      dispatch(upsertBoard(res.data));
      return res.data;
    });

  return {
    boards,
    loaded,
    refreshBoards,
    createBoard,
    updateBoard,
    deleteBoard,
    createColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
  };
};

export default useBoards;
